import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  ProductOverview,
  KeyFeatures,
  Applications,
  WhyChooseLiftile,
  TechnicalSpecificationTable,
  MastSectionDetails,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
  ComparisonTable,
  IndustriesWeServe,
  QualitySafetyCommitment,
} from "../components/product";
import {
  ArrowUp,
  Award,
  Boxes,
  Building,
  Building2,
  Cable,
  CircuitBoard,
  ClipboardCheck,
  Cog,
  Cpu,
  Factory,
  Flag,
  Frame,
  Gauge,
  GraduationCap,
  HardHat,
  Headphones,
  Hotel,
  Layers,
  ListChecks,
  Package,
  ShieldCheck,
  Siren,
  Sparkles,
  Stethoscope,
  Users,
  Zap,
} from "lucide-react";

/* =====================================================================
   PASSENGER CUM MATERIAL HOIST (LPMH Series) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-passenger-hoist.jpg";

const HERO_SPECS = [
  { Icon: ArrowUp, value: "Up to 150 m", label: "Maximum Height" },
  { Icon: Package, value: "Up to 2000 kg", label: "Rated Capacity" },
  { Icon: Zap, value: "30 m/min", label: "Lifting Speed" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "High Rise Ready",
  "Rack & Pinion Drive",
  "Advanced Safety",
  "Premium Components",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-passenger-hoist.jpg",
    caption: "LPMH Series — LIFTILE studio view of the twin-cage assembly",
  },
  {
    src: "/pch-cage-outdoor.png",
    caption: "LPMH Passenger cum Material Hoist mounted on a high-rise concrete structure",
  },
  {
    src: "/pch-cage-inside.png",
    caption: "Inside the LPMH cage — safety instructions, control panel and diamond-plate flooring",
  },
];

const MODELS = {
  LPMH1000: {
    name: "LPMH1000",
    tagline: "Efficient 1000 kg / 12-person model for mid-rise construction",
    rows: [
      { label: "Rated Capacity", value: "1000 kg / 12 persons" },
      { label: "Speed", value: "36 m/min" },
      { label: "Maximum Height", value: "100 m" },
      { label: "Safety Device", value: "SAJ 40-1.2 (Overspeed Governor)" },
      { label: "Mast Size", value: "650 × 650 mm" },
      { label: "Mast Section", value: "Square 450 × 450 mm × 1.5 m" },
      { label: "Round Tube", value: "Ø60 × 4.5 mm Thick" },
      { label: "Wall Tie Distance", value: "Up to 6 m" },
      { label: "Cage Size", value: "3.0 × 1.3 × 2.4 m (L × W × H)" },
      { label: "Floor Selection", value: "Optional" },
      { label: "Load Sensor", value: "Standard" },
    ],
  },
  LPMH1500: {
    name: "LPMH1500",
    tagline: "Best-selling 1500 kg / 18-person model for residential towers",
    rows: [
      { label: "Rated Capacity", value: "1500 kg / 18 persons" },
      { label: "Speed", value: "36 m/min" },
      { label: "Maximum Height", value: "150 m" },
      { label: "Safety Device", value: "SAJ 40-1.2 (Overspeed Governor)" },
      { label: "Mast Size", value: "650 × 650 mm" },
      { label: "Mast Section", value: "Square 450 × 450 mm × 1.5 m" },
      { label: "Round Tube", value: "Ø60 × 4.5 mm Thick" },
      { label: "Wall Tie Distance", value: "Up to 6 m" },
      { label: "Cage Size", value: "3.2 × 1.5 × 2.4 m (L × W × H)" },
      { label: "Floor Selection", value: "Standard" },
      { label: "Load Sensor", value: "Standard" },
    ],
  },
  LPMH2000: {
    name: "LPMH2000",
    tagline: "Heavy-duty 2000 kg / 24-person flagship for high-rise projects",
    rows: [
      { label: "Rated Capacity", value: "2000 kg / 24 persons" },
      { label: "Speed", value: "40 m/min" },
      { label: "Maximum Height", value: "150 m" },
      { label: "Safety Device", value: "SAJ 50-1.4 (Overspeed Governor)" },
      { label: "Mast Section", value: "650 × 650 × 1508 mm" },
      { label: "Wall Tie Distance", value: "Up to 6 m" },
      { label: "Cage Size", value: "3.6 × 1.5 × 2.4 m (L × W × H)" },
      { label: "Floor Selection", value: "Standard" },
      { label: "Load Sensor", value: "Standard" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Cog,
    title: "Rack & Pinion Drive System",
    desc: "Precision-machined rack and pinion gear system delivers smooth, positive and reliable vertical movement even under full load.",
  },
  {
    Icon: Users,
    title: "Twin / Single Cage Configuration",
    desc: "Available with single or twin-cage arrangements to suit high-rise passenger movement and material handling requirements.",
  },
  {
    Icon: ShieldCheck,
    title: "SAJ Overspeed Governor",
    desc: "Premium SAJ safety device continuously monitors travel speed and engages the safety brake to prevent free-fall.",
  },
  {
    Icon: Gauge,
    title: "Automatic Load Sensor",
    desc: "Real-time load monitoring prevents overloading and protects the machine, structure and personnel on every lift.",
  },
  {
    Icon: Siren,
    title: "Emergency Stop System",
    desc: "Dedicated emergency stop halts machine operation instantly across the cage, base and control panel.",
  },
  {
    Icon: CircuitBoard,
    title: "Premium Electrical Control Panel",
    desc: "ABB and Schneider Electric components deliver dependable control, reliable protection and long-term durability.",
  },
  {
    Icon: ListChecks,
    title: "Automatic Floor Selection",
    desc: "Optional automatic floor selection system improves operational efficiency on tall buildings and multi-storey projects.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LPMH machine undergoes trial run, safety verification and quality inspection prior to dispatch.",
  },
  {
    Icon: Layers,
    title: "Hot Dip Galvanized Mast Sections",
    desc: "Hot Dip Galvanized Mast Sections for Superior Corrosion Protection.",
  },
];

const COMPONENTS = [
  {
    Icon: Boxes,
    title: "Cage",
    desc: "Heavy-duty steel cage engineered for safe transport of passengers and construction materials with premium finish.",
  },
  {
    Icon: Layers,
    title: "Mast Section",
    desc: "650 × 650 × 1508 mm modular mast sections form the vertical tower with integrated rack profile.",
  },
  {
    Icon: Cog,
    title: "Rack & Pinion Drive",
    desc: "Precision-machined rack teeth engage with pinion gears for controlled and positive vertical travel.",
  },
  {
    Icon: Cpu,
    title: "Brake Motor Assembly",
    desc: "Premium branded gearbox + brake motor unit for safe stopping, precise positioning and reliable operation.",
  },
  {
    Icon: ShieldCheck,
    title: "SAJ Safety Device",
    desc: "Overspeed governor engages a mechanical brake automatically if travel speed exceeds safe limits.",
  },
  {
    Icon: CircuitBoard,
    title: "Electrical Cabinet",
    desc: "ABB / Schneider Electric electricals housed in an IP-rated cabinet with premium switchgear and contactors.",
  },
  {
    Icon: Cable,
    title: "Wall Tie System",
    desc: "Structural wall-ties secure the mast to the building at intervals of up to 6 m for high-rise stability.",
  },
  {
    Icon: Frame,
    title: "Base Frame & Landing",
    desc: "Robust base frame with landing gates, safety mesh and ground-level access controls for safe boarding.",
  },
];

const APPLICATIONS = [
  {
    Icon: Building,
    title: "High-Rise Residential Towers",
    desc: "Safe passenger and material lifting for tall residential complexes and apartment towers.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Reliable vertical transport for office towers, shopping malls and mixed-use complexes.",
  },
  {
    Icon: Hotel,
    title: "Hotels & Hospitality",
    desc: "Efficient movement of workers, materials and finishing crews on hotel and resort projects.",
  },
  {
    Icon: Stethoscope,
    title: "Hospitals & Healthcare",
    desc: "Consistent lifting solution for multi-storey hospital campuses and healthcare infrastructure.",
  },
  {
    Icon: GraduationCap,
    title: "Educational Institutions",
    desc: "Dependable hoisting on schools, universities and institutional campus projects.",
  },
  {
    Icon: Factory,
    title: "Industrial & Infrastructure",
    desc: "Passenger + material lifting on heavy industry, refineries, power plants and infrastructure sites.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Built for High-Rise",
    desc: "Engineered for demanding construction environments up to 150 metres.",
  },
  {
    Icon: ShieldCheck,
    title: "Multi-Layer Safety",
    desc: "Overspeed governor, load sensor, limit switches and emergency stop working in unison.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded electricals and hardware including ABB, Schneider Electric and SAJ.",
  },
  {
    Icon: Users,
    title: "Passenger & Material",
    desc: "Purpose-built for combined personnel movement and construction material transport.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every machine is trial-run and quality inspected before dispatch to site.",
  },
  {
    Icon: Headphones,
    title: "Nationwide Support",
    desc: "Installation guidance, technical assistance and after-sales support across India.",
  },
];

const TECH_SPECS = [
  {
    parameter: "Rated Capacity",
    values: ["1000 kg / 12 persons", "1500 kg / 18 persons", "2000 kg / 24 persons"],
  },
  { parameter: "Lifting Speed", values: ["36 m/min", "36 m/min", "40 m/min"] },
  { parameter: "Maximum Height", values: ["100 m", "150 m", "150 m"] },
  { parameter: "Drive System", values: ["Rack & Pinion", "Rack & Pinion", "Rack & Pinion"] },
  { parameter: "Safety Device", values: ["SAJ 40-1.2", "SAJ 40-1.2", "SAJ 50-1.4"] },
  {
    parameter: "Mast Section",
    values: [
      "650 × 650 × 1508 mm",
      "650 × 650 × 1508 mm",
      "650 × 650 × 1508 mm",
    ],
  },
  {
    parameter: "Cage Size (L × W × H)",
    values: ["3.0 × 1.3 × 2.4 m", "3.2 × 1.5 × 2.4 m", "3.6 × 1.5 × 2.4 m"],
  },
  {
    parameter: "Wall Tie Distance",
    values: ["Up to 6 m", "Up to 6 m", "Up to 6 m"],
  },
  {
    parameter: "Floor Selection",
    values: ["Optional", "Standard", "Standard"],
  },
];

const ELECTRICAL_SPECS = [
  { parameter: "Power Supply", values: ["3-Phase, 415 V AC, 50 Hz"] },
  {
    parameter: "Drive Motor",
    values: ["3 × 11 kW (single cage) / 6 × 11 kW (twin cage), Brake Motor"],
  },
  { parameter: "Control System", values: ["PLC based with premium contactors and relays"] },
  {
    parameter: "Main Control Panel",
    values: ["ABB / Schneider Electric switchgear housed in IP-rated cabinet"],
  },
  {
    parameter: "Cabin Control Panel",
    values: ["Wired push-button + emergency stop + floor indicator"],
  },
  { parameter: "Power Cable", values: ["Polycab / equivalent premium multi-core trailing cable"] },
  {
    parameter: "Limit Switches",
    values: ["Upper & lower travel limit + final limit safety cutoff"],
  },
  {
    parameter: "Overload Protection",
    values: ["Automatic load-sensor cutoff at rated capacity"],
  },
  {
    parameter: "Emergency Stop",
    values: ["Cage + base + main panel — instant halt on activation"],
  },
];

const MAST_DETAILS = [
  { parameter: "Mast Size", value: "650 × 650 mm" },
  { parameter: "Mast Section", value: "Square 450 × 450 mm × 1.5 m" },
  { parameter: "Round Tube", value: "Ø60 × 4.5 mm Thick" },
  { parameter: "Steel Grade", value: "High-tensile structural steel" },
  { parameter: "Rack Module", value: "M8, precision-machined" },
  { parameter: "Connector Bolts", value: "8 × M24 Grade 8.8 high-strength bolts per joint" },
  { parameter: "Approximate Weight", value: "≈ 165 kg per mast section" },
  { parameter: "Wall Tie Spacing", value: "Every 6 m along the mast height" },
  { parameter: "Free Standing Height", value: "Up to 9 m without wall ties" },
  { parameter: "Surface Treatment", value: "Hot Dip Galvanized for superior corrosion protection" },
];

const FAQS = [
  {
    q: "What is a Passenger Cum Material Hoist?",
    a: "A Passenger Cum Material Hoist is a rack-and-pinion driven construction elevator designed to transport both site personnel and construction materials safely up multi-storey buildings during construction and finishing phases.",
  },
  {
    q: "What is the maximum lifting height of the LPMH Series?",
    a: "The LIFTILE LPMH Series supports lifting heights up to 150 metres depending on the model and project configuration.",
  },
  {
    q: "How is a Passenger Hoist different from a Tower Hoist?",
    a: "A Passenger Cum Material Hoist uses a rack & pinion drive with an enclosed cage and is certified to lift personnel + material.\n\nA Tower Hoist uses a wire-rope drive with an open platform / bucket and is intended for material lifting only.",
  },
  {
    q: "What safety features are included?",
    a: "The LPMH Series includes SAJ overspeed governor, automatic load sensor, upper + lower travel limits, final limit safety cut-off, emergency stop across cage / base / main panel and premium ABB & Schneider Electric electricals.",
  },
  {
    q: "Do you provide installation and after-sales support?",
    a: "Yes.\n\nLIFTILE provides installation guidance, on-site commissioning support, spare parts and after-sales technical service across India.",
  },
  {
    q: "Can the cage be customised for larger materials?",
    a: "Yes.\n\nLPMH1500 and LPMH2000 cages are sized for larger material payloads and can be supplied with optional interlocked landing gates, floor selection and additional finishes based on project requirements.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "multi-functional-tower-hoist",
    name: "Multi Functional Tower Hoist",
    desc: "LMTB Series flagship tower hoist with interchangeable buckets, rebar carrier and heights up to 125 m.",
    image: "/multi-functional-tower-hoist-catalog.png",
  },
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "LTC Series wire-rope tower hoist for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/conventional-tower-hoist-site-2.jpeg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact mini jib crane for slab-level material movement — ideal for tight sites and finishing phases.",
    image: "/product-monkey-lift.jpg",
  },
];

const COMPARISON_ROWS = [
  { feature: "Passenger Lifting", left: false, right: true },
  { feature: "Material Lifting", left: true, right: true },
  { feature: "Drive System", left: "Wire Rope", right: "Rack & Pinion" },
  { feature: "Enclosed Cage", left: false, right: true },
  { feature: "Overspeed Governor (SAJ)", left: false, right: true },
  { feature: "Automatic Load Sensor", left: "Optional", right: true },
  { feature: "Floor Selection System", left: false, right: "Standard" },
  { feature: "Maximum Height", left: "65 m", right: "150 m" },
  { feature: "Rated Capacity", left: "500–1500 kg", right: "1000–2000 kg" },
  { feature: "Lifting Speed", left: "30–35 m/min", right: "36–40 m/min" },
  { feature: "Certified for Personnel", left: false, right: true },
  { feature: "Ideal For", left: "Material Only", right: "High-Rise Projects" },
];

const INDUSTRIES = [
  {
    Icon: Building,
    title: "Residential High-Rise",
    desc: "Passenger + material lifting for tall apartment towers and residential campuses.",
  },
  {
    Icon: Building2,
    title: "Commercial Towers",
    desc: "Offices, malls, business parks and mixed-use commercial developments.",
  },
  {
    Icon: Hotel,
    title: "Hotels & Hospitality",
    desc: "Efficient vertical transport for hotel towers and hospitality projects.",
  },
  {
    Icon: Stethoscope,
    title: "Hospitals & Healthcare",
    desc: "Certified passenger movement for multi-storey healthcare campuses.",
  },
  {
    Icon: GraduationCap,
    title: "Educational Institutions",
    desc: "Dependable hoisting for schools, colleges and university projects.",
  },
  {
    Icon: Factory,
    title: "Industrial Plants",
    desc: "Reliable lifting on refineries, power plants and heavy industry.",
  },
  {
    Icon: Layers,
    title: "Infrastructure Projects",
    desc: "Metro, treatment plants, ports and public infrastructure builds.",
  },
  {
    Icon: Package,
    title: "Warehouse Construction",
    desc: "Fast material and personnel movement on large-format warehouses.",
  },
  {
    Icon: ArrowUp,
    title: "Mixed-Use Skyscrapers",
    desc: "Vertical transport up to 150 metres for tall mixed-use developments.",
  },
];

const QUALITY_CARDS = [
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LPMH machine is personally inspected and signed off before leaving the factory.",
  },
  {
    Icon: ShieldCheck,
    title: "Trial Run & Safety Verified",
    desc: "Full-load trial run + brake, overspeed and limit switch verification on every machine.",
  },
  {
    Icon: Award,
    title: "Premium Branded Components",
    desc: "ABB, Schneider Electric, SAJ, Polycab and other trusted brands used throughout.",
  },
  {
    Icon: Layers,
    title: "Heavy Duty Fabrication",
    desc: "Precision-welded high-tensile steel mast sections engineered for long service life.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coating",
    desc: "7-tank surface treatment with durable powder coating for site-grade corrosion resistance.",
  },
  {
    Icon: Headphones,
    title: "Nationwide Technical Support",
    desc: "Installation, spares, service and technical assistance across every project geography in India.",
  },
];

const CTA_BADGES = [
  "Up to 150 m Height",
  "Rack & Pinion Drive",
  "SAJ Safety Governor",
  "Nationwide Support",
];

/* ---------- LPMH Mast Section Blueprint SVG (Section 10) ---------- */
const LPMH_MAST_BLUEPRINT = (
  <svg
    aria-hidden
    viewBox="0 0 320 400"
    className="absolute inset-0 w-full h-full opacity-70 pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.4"
  >
    <rect x="90" y="40" width="140" height="320" strokeDasharray="0" />
    {[0, 1, 2, 3, 4].map((i) => (
      <g key={i}>
        <line x1="90" y1={40 + i * 64} x2="230" y2={40 + (i + 1) * 64} />
        <line x1="230" y1={40 + i * 64} x2="90" y2={40 + (i + 1) * 64} />
        <line
          x1="90"
          y1={40 + (i + 1) * 64}
          x2="230"
          y2={40 + (i + 1) * 64}
          strokeDasharray="4 4"
          opacity="0.6"
        />
      </g>
    ))}
    <line x1="230" y1="40" x2="230" y2="360" strokeWidth="2.4" />
    {Array.from({ length: 16 }).map((_, i) => (
      <line key={i} x1="230" y1={45 + i * 20} x2="245" y2={45 + i * 20} />
    ))}
    <line x1="60" y1="40" x2="60" y2="360" strokeDasharray="2 4" />
    <text
      x="30"
      y="205"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.85"
    >
      1508 mm
    </text>
    <line x1="90" y1="380" x2="230" y2="380" strokeDasharray="2 4" />
    <text
      x="130"
      y="395"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.85"
    >
      650 × 650 mm
    </text>
  </svg>
);

/* ---------- LPMH Twin-Cage Brochure Blueprint SVG (Section 11) ---------- */
const LPMH_BROCHURE_BLUEPRINT = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    <rect x="185" y="20" width="30" height="340" strokeDasharray="0" />
    <line x1="200" y1="20" x2="200" y2="360" strokeDasharray="4 4" opacity="0.6" />
    <rect x="70" y="240" width="115" height="120" />
    <rect x="215" y="240" width="115" height="120" />
    <line x1="127.5" y1="240" x2="127.5" y2="360" strokeDasharray="3 3" />
    <line x1="272.5" y1="240" x2="272.5" y2="360" strokeDasharray="3 3" />
    {Array.from({ length: 14 }).map((_, i) => (
      <line key={i} x1="215" y1={30 + i * 22} x2="228" y2={30 + i * 22} />
    ))}
    <rect x="55" y="360" width="290" height="24" />
    <line x1="30" y1="20" x2="30" y2="360" strokeDasharray="2 4" />
    <text
      x="5"
      y="200"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      150m
    </text>
  </svg>
);

export default function PassengerCumMaterialHoistPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Passenger Cum Material Hoist" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lpmh-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LPMH Series"
        title={
          <>
            Passenger Cum
            <br />
            Material Hoist
            <br />
            <span className="text-liftile-orange">(Rack &amp; Pinion)</span>
          </>
        }
        subtitle="Engineered for safe, smooth and reliable vertical transportation of passengers and construction materials with advanced rack and pinion technology."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lpmh-gallery"
        heading={
          <>
            LPMH Series{" "}
            <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LPMH Series Passenger Cum Material Hoist deployed on live construction sites across India."
        items={GALLERY}
        seriesLabel="LPMH Series"
        imageAlt="LIFTILE LPMH Series Passenger Cum Material Hoist"
      />

      {/* ─── SECTION 3: QUICK SPECS (centered layout — no summary cards) ─── */}
      <QuickSpecsTabs
        testId="lpmh-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Compare configurations instantly — the specifications table updates as you switch between LPMH1000, LPMH1500 and LPMH2000."
        models={MODELS}
        defaultModel="LPMH1500"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lpmh-features"
        heading={
          <>
            Built for Safety.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Reliability.
            </span>
          </>
        }
        subtitle="The LIFTILE LPMH Series combines advanced rack & pinion drive technology, multi-layer safety systems and premium branded components to deliver dependable performance on high-rise construction projects."
        items={FEATURES}
      />

      {/* ─── SECTION 5: MAIN COMPONENTS (dark ProductOverview) ─── */}
      <ProductOverview
        testId="lpmh-components"
        heading={
          <>
            Engineered from the{" "}
            <span className="text-liftile-orange">Ground Up</span>
          </>
        }
        subtitle="A closer look at the core sub-assemblies that make the LPMH Series a safe, dependable and long-lasting construction elevator."
        items={COMPONENTS}
      />

      {/* ─── SECTION 6: APPLICATIONS (light variant) ─── */}
      <Applications
        testId="lpmh-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">High-Rise Project</span>
          </>
        }
        subtitle="From residential towers to industrial plants, the LPMH Series delivers safe passenger and material lifting across a wide range of construction environments."
        items={APPLICATIONS}
        variant="light"
      />

      {/* ─── SECTION 7: WHY BUILDERS CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lpmh-why-choose"
        image="/product-passenger-hoist.jpg"
        imageAlt="LIFTILE LPMH Series Passenger Cum Material Hoist"
        imageCaption="LPMH Series delivering safe, reliable passenger and material lifting on high-rise construction sites."
        floatingBadge={{ top: "Up to", bottom: "150 metres" }}
        eyebrow="Why LPMH Series"
        heading={
          <>
            Why Builders <br className="hidden sm:block" />
            <span className="text-liftile-orange">Choose LIFTILE</span>
          </>
        }
        subtitle="The LPMH Series is engineered to maximize safety, reliability and productivity on demanding high-rise construction projects across India."
        trustCards={TRUST_CARDS}
        background="cream"
      />

      {/* ─── SECTION 8: TECHNICAL SPECIFICATIONS (dark) ─── */}
      <TechnicalSpecificationTable
        testId="lpmh-tech-specs"
        heading={
          <>
            Technical{" "}
            <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Compare all LPMH Series configurations side-by-side and choose the ideal model for your project requirements."
        columns={["LPMH1000", "LPMH1500", "LPMH2000"]}
        rows={TECH_SPECS}
        minWidthClass="min-w-[720px]"
        maxWidthClass="max-w-5xl"
      />

      {/* ─── SECTION 9: ELECTRICAL SPECIFICATIONS (light, 2-col) ─── */}
      <TechnicalSpecificationTable
        testId="lpmh-electrical-specs"
        eyebrow="Electrical Data"
        heading={
          <>
            Electrical{" "}
            <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Premium branded electricals designed for dependable control, safety and long-term reliability across every LPMH Series machine."
        columns={["Specification"]}
        rows={ELECTRICAL_SPECS}
        variant="light"
        minWidthClass="min-w-[560px]"
        footerNote="Electrical configuration may be customised based on site requirements and project specifications."
      />

      {/* ─── SECTION 10: MAST SECTION DETAILS ─── */}
      <MastSectionDetails
        testId="lpmh-mast-details"
        eyebrow="Mast Details"
        heading={
          <>
            Precision Modular{" "}
            <span className="text-liftile-orange">Mast System</span>
          </>
        }
        subtitle="Each mast section is fabricated from high-tensile structural steel with a precision-machined rack profile — engineered for positive engagement, high strength and long service life."
        blueprint={
          <img
            src="/pmh-mast-sections.jpeg"
            alt="LIFTILE LPMH Series hot-dip galvanized modular mast sections at the factory"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        }
        blueprintCaption={{
          eyebrow: "Modular Mast System",
          text: "Hot Dip Galvanized modular mast — 650 × 650 mm size, Square 450 × 450 × 1.5 m sections, Ø60 × 4.5 mm round tube.",
        }}
        specs={MAST_DETAILS}
      />

      {/* ─── SECTION 11: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lpmh-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LPMH Series technical catalogue — including specifications, safety systems, drawings and configuration options."
        blueprintSvg={LPMH_BROCHURE_BLUEPRINT}
      />

      {/* ─── SECTION 12: FAQ ─── */}
      <PremiumFAQ
        testId="lpmh-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Find answers to the most common questions about the LPMH Series passenger cum material hoist."
        items={FAQS}
      />

      {/* ─── SECTION 13: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lpmh-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 14: COMPARISON TABLE ─── */}
      <ComparisonTable
        testId="lpmh-comparison"
        heading={
          <>
            Passenger Hoist vs.{" "}
            <span className="text-liftile-orange">
              Conventional Tower Hoist
            </span>
          </>
        }
        subtitle="Two very different machines for two very different jobs. Here's how the LPMH Series compares against a conventional wire-rope tower hoist."
        leftColumnHeader="Conventional Tower Hoist"
        rightColumnHeader="LPMH Series"
        rows={COMPARISON_ROWS}
        footerNote="The LPMH Series is designed for high-rise projects that require certified passenger transport in addition to construction material movement."
        background="cream"
      />

      {/* ─── SECTION 15: INDUSTRIES WE SERVE ─── */}
      <IndustriesWeServe
        testId="lpmh-industries"
        heading={
          <>
            Trusted Across Every{" "}
            <span className="text-liftile-orange">Construction Project</span>
          </>
        }
        subtitle="The LPMH Series delivers dependable passenger and material lifting performance across a wide range of construction environments in India and beyond."
        items={INDUSTRIES}
      />

      {/* ─── SECTION 16: QUALITY & SAFETY COMMITMENT ─── */}
      <QualitySafetyCommitment
        testId="lpmh-quality"
        heading={
          <>
            Built Around Quality{" "}
            <span className="text-liftile-orange">and Safety</span>
          </>
        }
        subtitle="Every LPMH Series machine is manufactured using premium materials, trusted branded components and strict quality inspections before dispatch."
        items={QUALITY_CARDS}
      />

      {/* ─── SECTION 17: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lpmh-product-cta"
        backgroundImage="/project-thiruvananthapuram.png"
        backgroundAlt="LIFTILE LPMH Series on a live construction site"
        heading={
          <>
            Ready to Lift{" "}
            <span className="text-liftile-orange">
              Higher, Safer, Smarter?
            </span>
          </>
        }
        subtitle="Discover how the LIFTILE LPMH Series can improve safety, productivity and project timelines on your next high-rise construction build."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{
          label: "Talk to Our Engineering Team",
          href: "/contact",
        }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
